import React, { useState } from "react";
import { AlertTriangle, ArrowLeft, Fingerprint, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { apiUrl } from "../api";
import { decodeRequestOptions, isWebAuthnSupported, serializeCredential } from "../utils/webauthn";

const TEXT = {
  en: {
    title: "Two-Factor Authentication",
    totpHint: "Enter the 6-digit code from your authenticator app.",
    recoveryHint: "Enter one recovery code. Each recovery code can be used once.",
    codeLabel: "Authenticator code",
    recoveryLabel: "Recovery code",
    verify: "Verify",
    verifying: "Verifying...",
    useRecovery: "Use a recovery code",
    useAuthenticator: "Use authenticator code",
    cancel: "Back to login",
    required: "Enter the verification code.",
    invalidChallenge: "Authentication expired. Please sign in again.",
    failed: "Verification failed. Check the code and try again.",
    network: "Network error.",
    usePasskey: "Use passkey",
    passkeyHint: "Use a passkey registered for this Super Admin account.",
    passkeyUnsupported: "This browser does not support passkeys.",
    passkeyFailed: "Passkey verification failed.",
    passkeyPrompt: "Follow your browser prompt to verify with a passkey.",
  },
  he: {
    title: "אימות דו-שלבי",
    totpHint: "הזן את הקוד בן 6 הספרות מאפליקציית האימות שלך.",
    recoveryHint: "הזן קוד שחזור אחד. כל קוד שחזור ניתן לשימוש פעם אחת בלבד.",
    codeLabel: "קוד אימות",
    recoveryLabel: "קוד שחזור",
    verify: "אמת",
    verifying: "מאמת...",
    useRecovery: "שימוש בקוד שחזור",
    useAuthenticator: "שימוש בקוד מאפליקציית אימות",
    cancel: "חזרה להתחברות",
    required: "יש להזין קוד אימות.",
    invalidChallenge: "פג תוקף האימות. יש להתחבר מחדש.",
    failed: "האימות נכשל. בדוק את הקוד ונסה שוב.",
    network: "שגיאת תקשורת.",
    usePasskey: "שימוש במפתח גישה",
    passkeyHint: "השתמש במפתח גישה הרשום לחשבון Super Admin זה.",
    passkeyUnsupported: "הדפדפן הזה אינו תומך במפתחות גישה.",
    passkeyFailed: "אימות מפתח הגישה נכשל.",
    passkeyPrompt: "עקוב אחר הנחיית הדפדפן כדי לאמת עם מפתח גישה.",
  },
};

const normalizeLanguage = (language) => (language === "en" ? "en" : "he");

const MfaVerification = ({ challengeToken, passkeyAvailable = false, language = "he", onVerified, onCancel }) => {
  const uiLanguage = normalizeLanguage(language);
  const t = TEXT[uiLanguage];
  const isRtl = uiLanguage === "he";
  const [mode, setMode] = useState("totp");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    const cleanCode = mode === "totp" ? code.replace(/\D/g, "") : code.trim();
    if (!challengeToken || !cleanCode || (mode === "totp" && cleanCode.length !== 6)) {
      setError(t.required);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(apiUrl("/auth?action=verify_mfa"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenge_token: challengeToken,
          code: cleanCode,
          method: mode === "recovery" ? "recovery_code" : "totp",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.token) {
        onVerified(data);
        return;
      }
      const message = String(data?.error || data?.message || "");
      setError(/challenge|expired|token/i.test(message) ? t.invalidChallenge : t.failed);
    } catch {
      setError(t.network);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode((current) => (current === "totp" ? "recovery" : "totp"));
    setCode("");
    setError("");
  };

  const verifyWithPasskey = async () => {
    if (!challengeToken) {
      setError(t.invalidChallenge);
      return;
    }
    if (!isWebAuthnSupported()) {
      setError(t.passkeyUnsupported);
      return;
    }

    setPasskeyLoading(true);
    setError("");
    try {
      const optionsResponse = await fetch(apiUrl("/auth?action=passkey_authentication_options"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge_token: challengeToken }),
      });
      const optionsData = await optionsResponse.json().catch(() => ({}));
      if (!optionsResponse.ok || !optionsData?.options || !optionsData?.challenge_id) {
        throw new Error(optionsData?.error || t.passkeyFailed);
      }

      const credential = await navigator.credentials.get({
        publicKey: decodeRequestOptions(optionsData.options),
      });
      if (!credential) {
        throw new Error(t.passkeyFailed);
      }

      const verifyResponse = await fetch(apiUrl("/auth?action=passkey_authentication_verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenge_token: challengeToken,
          challenge_id: optionsData.challenge_id,
          credential: serializeCredential(credential),
        }),
      });
      const verifyData = await verifyResponse.json().catch(() => ({}));
      if (verifyResponse.ok && verifyData?.token) {
        onVerified(verifyData);
        return;
      }
      throw new Error(verifyData?.error || t.passkeyFailed);
    } catch (err) {
      setError(String(err?.message || t.passkeyFailed));
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" dir={isRtl ? "rtl" : "ltr"}>
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-500/15 text-emerald-300">
          <ShieldCheck size={32} />
        </div>
        <h2 className="text-2xl font-black text-white">{t.title}</h2>
        <p className="mt-2 text-sm font-bold leading-6 text-slate-300">
          {mode === "totp" ? t.totpHint : t.recoveryHint}
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <label className="block">
        <span className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-slate-400">
          {mode === "totp" ? t.codeLabel : t.recoveryLabel}
        </span>
        <input
          value={code}
          onChange={(event) =>
            setCode(mode === "totp" ? event.target.value.replace(/\D/g, "").slice(0, 6) : event.target.value)
          }
          inputMode={mode === "totp" ? "numeric" : "text"}
          pattern={mode === "totp" ? "[0-9]*" : undefined}
          autoComplete="one-time-code"
          autoFocus
          className="w-full rounded-2xl border border-white/10 bg-slate-900/70 px-4 py-4 text-center text-2xl font-black tracking-[0.25em] text-white outline-none transition focus:border-emerald-400"
        />
      </label>

      <button
        type="submit"
        disabled={loading || passkeyLoading}
        className="flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-emerald-400 disabled:opacity-60"
      >
        {loading && <Loader2 size={18} className="animate-spin" />}
        {loading ? t.verifying : t.verify}
      </button>

      {passkeyAvailable && (
        <div className="rounded-2xl border border-cyan-300/15 bg-cyan-300/10 p-4 text-center">
          <p className="mb-3 text-xs font-bold leading-5 text-cyan-100">{passkeyLoading ? t.passkeyPrompt : t.passkeyHint}</p>
          <button
            type="button"
            onClick={verifyWithPasskey}
            disabled={loading || passkeyLoading}
            className="inline-flex w-full items-center justify-center gap-3 rounded-2xl border border-cyan-200/20 bg-white/10 px-5 py-3 text-sm font-black text-cyan-50 transition hover:bg-white/15 disabled:opacity-60"
          >
            {passkeyLoading ? <Loader2 size={18} className="animate-spin" /> : <Fingerprint size={18} />}
            {t.usePasskey}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-black">
        <button type="button" onClick={switchMode} className="flex items-center gap-2 text-emerald-300 hover:text-white">
          <KeyRound size={16} />
          {mode === "totp" ? t.useRecovery : t.useAuthenticator}
        </button>
        <button type="button" onClick={onCancel} className="flex items-center gap-2 text-slate-300 hover:text-white">
          <ArrowLeft size={16} />
          {t.cancel}
        </button>
      </div>
    </form>
  );
};

export default MfaVerification;
